import React from "react";

import { FormBox, FormField } from "@impero-com/ui";
import { Checkbox } from "antd";
import { cx } from "class-variance-authority";

import { ERRORED_CLASSNAME } from "../../../utils/form/erroredElements";
import { useFormAtom } from "../../../utils/form/formAtom/hooks";
import { FormAtom } from "../../../utils/form/formAtom/types";
import { assertNever } from "../../../utils/obj";

import * as theme from "./theme.css";

export type CheckboxInputType = boolean | "indeterminate";

export interface FACheckboxInputProps<T> {
    atom: FormAtom<T>;
    label?: React.ReactNode;
    disabled?: boolean;
}

/**
 * Basic form component based on ant design's Checkbox
 *
 * Supports indeterminate state; using "indeterminate" value
 * Note: the checkbox itself will only yield booleans
 */
export function FACheckboxInput<T extends CheckboxInputType>({
    atom,
    label,
    disabled = false,
}: FACheckboxInputProps<T>) {
    const { value, error, dispatchValue } = useFormAtom(atom);

    const { checked, indeterminate } = (() => {
        switch (value) {
            case true:
                return { checked: true, indeterminate: undefined };
            case false:
                return { checked: false, indeterminate: undefined };
            case "indeterminate":
                return { checked: undefined, indeterminate: true };
            default:
                assertNever(value);
        }
    })();

    return (
        <FormBox>
            <FormField
                error={error?.value}
                className={cx(
                    theme.checkboxLineHeight,
                    error?.value && ERRORED_CLASSNAME
                )}
                lite
            >
                <Checkbox
                    checked={checked}
                    indeterminate={indeterminate}
                    disabled={disabled}
                    onChange={(e) => void dispatchValue(e.target.checked as T)}
                >
                    {label}
                </Checkbox>
            </FormField>
        </FormBox>
    );
}
